// src/providers/authProvider.ts - SOLUCIÓN ELEGANTE
import { AuthProvider } from 'react-admin';
import { 
  saveAuthData, 
  clearAuthData, 
  isAuthenticated, 
  getCurrentUser,
  extractTokenData 
} from '@/utils/auth';
import { API_CONFIG } from '@/config/constants';

interface LoginResponse {
  status: string;
  data: {
    token: string;
    user: {
      id: string;
      name: string;
      email: string;
      role: string;
    };
    expiresIn: number;
  };
}

interface ErrorResponse {
  message?: string;
  error?: string;
  statusCode?: number;
}

const apiUrl = API_CONFIG.BASE_URL;

// ✅ Event emitter para notificar cambios de autenticación
export const authEvents = new EventTarget();

export const AUTH_EVENTS = {
  LOGIN_SUCCESS: 'auth:login:success',
  LOGOUT: 'auth:logout',
  ROLE_CHANGED: 'auth:role:changed',
} as const;

const authProvider: AuthProvider = {
  login: async ({ username, password }: { username: string; password: string }) => {
    const request = new Request(`${apiUrl}/auth/login`, {
      method: 'POST',
      body: JSON.stringify({ email: username, password }),
      headers: new Headers({ 'Content-Type': 'application/json' }),
    });

    try {
      const response = await fetch(request);
      
      if (response.status < 200 || response.status >= 300) {
        const error = await response.json() as ErrorResponse;
        throw new Error(error.message || error.error || response.statusText);
      }
      
      const responseData = await response.json() as LoginResponse;
      const { token, user, expiresIn } = responseData.data;

      const tokenData = extractTokenData(token);
      
      // Guardar datos de autenticación
      saveAuthData({
        token,
        user: {
          ...user,
          role: user.role as any,
          hotelId: tokenData?.hotelId,
          groupId: tokenData?.groupId,
        },
        expiresIn,
      });

      if (tokenData?.hotelName) {
        localStorage.setItem('hotelName', tokenData.hotelName);
      }
      if (tokenData?.groupName) {
        localStorage.setItem('groupName', tokenData.groupName);
      }

      // ✅ Emitir evento de login exitoso
      authEvents.dispatchEvent(new CustomEvent(AUTH_EVENTS.LOGIN_SUCCESS, {
        detail: { user: responseData.data.user, role: user.role }
      }));

      // Indicar a React Admin que redirija al dashboard
      return Promise.resolve({ redirectTo: '/' });
      
    } catch (error) {
      console.error('Login error:', error);
      return Promise.reject(error);
    }
  },

  logout: async () => {
    try {
      const previousRole = localStorage.getItem('role');
      
      // Limpiar todos los datos de autenticación
      clearAuthData();
      localStorage.removeItem('hotelName');
      localStorage.removeItem('groupName');
      localStorage.removeItem('selectedHotelId');
      localStorage.removeItem('selectedHotelData');
      
      // ✅ Emitir evento de logout
      authEvents.dispatchEvent(new CustomEvent(AUTH_EVENTS.LOGOUT, {
        detail: { previousRole }
      }));
      
      // Redirigir al login
      return Promise.resolve('/login');
    } catch (error) {
      console.error('Logout error:', error);
      return Promise.reject(error);
    }
  },

  checkAuth: async () => {
    if (!isAuthenticated()) {
      return Promise.reject({ message: 'Not authenticated', redirectTo: '/login' });
    }
    
    const token = localStorage.getItem('token');
    if (!token) {
      return Promise.reject({ message: 'No token found', redirectTo: '/login' });
    }
    
    return Promise.resolve();
  },

  checkError: async (error: any) => {
    const status = error?.status || error?.response?.status;
    
    if (status === 401 || status === 403) {
      clearAuthData();
      localStorage.removeItem('hotelName');
      localStorage.removeItem('groupName');
      
      // ✅ Emitir evento de logout por error
      authEvents.dispatchEvent(new Event(AUTH_EVENTS.LOGOUT));
      
      return Promise.reject({ redirectTo: '/login' });
    }
    
    return Promise.resolve();
  },
  
  getPermissions: async () => {
    const role = localStorage.getItem('role');
    
    if (!role) {
      return Promise.reject({ message: 'No role found' });
    }
    
    // Retornar el rol directamente como string
    return Promise.resolve(role);
  },

  getIdentity: async () => {
    const user = getCurrentUser();
    
    if (!user || !user.id) {
      return Promise.reject({ message: 'No user identity found' });
    }
    
    return Promise.resolve({
      id: user.id,
      fullName: user.name || user.email || 'Usuario',
      avatar: undefined,
      email: user.email,
      role: user.role,
      hotelId: user.hotelId,
      groupId: user.groupId,
    });
  },

  handleCallback: async () => {
    return Promise.resolve();
  },
};

export default authProvider;